import { useState, useEffect, useCallback } from 'react'

import { ProductV2 } from '@common'
import cx from 'classnames'

import { ButtonProps, Button } from '@/components/buttons/Button'
import { ShoppingBagMedium, Lock } from '@/components/Icons'
import { FailureIconAnimated } from '@/components/Icons/Failure'
import { SuccessIconAnimated } from '@/components/Icons/Success'
import { useAddProduct } from '@/features/common/useAddProduct'

import styles from './BuyButton.module.scss'

function shouldUseDarkIcon(type: ButtonProps['type']) {
  switch (type) {
    case 'Secondary':
    case 'Tertiary':
    case 'OutlineSecondary':
      return true
    default:
      return false
  }
}

function getContent(
  isAdding: boolean,
  addedSuccesfully: boolean,
  type: ButtonProps['type'],
  content: React.ReactNode,
  isLocked?: boolean,
) {
  if (isLocked) {
    return <Lock className={cx(styles.Lock, { [styles.DarkLock]: shouldUseDarkIcon(type) })} />
  }

  if (isAdding) {
    return addedSuccesfully ? (
      <SuccessIconAnimated
        height={28}
        width={28}
        className={cx(styles.Icon, { [styles.DarkIcon]: shouldUseDarkIcon(type) })}
      />
    ) : (
      <FailureIconAnimated
        height={28}
        width={28}
        className={cx(styles.Icon, { [styles.DarkIcon]: shouldUseDarkIcon(type) })}
      />
    )
  }

  return (
    <>
      <ShoppingBagMedium className={cx(styles.Icon, { [styles.DarkIcon]: shouldUseDarkIcon(type) })} />
      {content}
    </>
  )
}

export const BuyButton: React.FC<ButtonProps & { isLocked?: boolean; product: ProductV2 }> = ({
  isLocked,
  product,
  onClick: originalOnClick,
  type,
  content,
  ...props
}) => {
  const addProductHandler = useAddProduct()
  const [isAdding, setIsAdding] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  useEffect(() => {
    if (isAdding) {
      const timeout = setTimeout(() => {
        setIsAdding(false)
        setIsSuccess(false)
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }, [isAdding])

  const onClick: ButtonProps['onClick'] = useCallback(
    async (e) => {
      e.currentTarget.style.width = `${e.currentTarget.offsetWidth}px`
      e.preventDefault()
      e.stopPropagation()
      const addResult = await addProductHandler(product)
      setIsSuccess(addResult)
      setIsAdding(true)
      originalOnClick?.(e)
    },
    [addProductHandler, originalOnClick, product],
  )
  return (
    <Button
      {...props}
      type={type}
      content={getContent(isAdding, isSuccess, type, content, isLocked)}
      onClick={onClick}
      data-type="buy"
    />
  )
}
